import React, { useMemo } from 'react'
import { useMessaging } from './MessagingProvider'
import MessageThread from './MessageThread'
import NewMessage from './NewMessage'
import ThreadList from './ThreadList'
import SentList, { SentMessage } from './SentList'
import {
  ResizableHandle,
  ResizablePanel,
  ResizablePanelGroup,
} from '~/lib/ui/resizable'
import { ScrollArea } from '~/lib/ui/scroll-area'
import { Tabs, TabsList, TabsTrigger, TabsContent } from '~/lib/ui/tabs'
import useTranslation from '~/components/shared/TranslationProvider'
import { Button } from '~/lib/ui/button'
import {
  Select,
  SelectContent,
  SelectTrigger,
  SelectValue,
  SelectItem,
} from '~/lib/ui/select'

const Layout = ({ layoutHeight = 'calc(100vh - 310px)' }) => {
  const { isMobile } = useMessaging()

  return isMobile ? (
    <MobileLayout />
  ) : (
    <DesktopLayout layoutHeight={layoutHeight} />
  )
}
export default Layout

const InboxSentTabs = () => {
  const { t } = useTranslation('CoachMessageThread')
  const { viewingSentMessages, setViewingSentMessages, clearNewMessageMode } =
    useMessaging()
  const handleTabChange = (value) => {
    setViewingSentMessages(value === 'sent')
    clearNewMessageMode()
    window.location.hash = ''
  }

  return (
    <Tabs
      value={viewingSentMessages ? 'sent' : 'inbox'}
      onValueChange={handleTabChange}
    >
      <TabsList className="ml-4 w-3/4">
        <TabsTrigger className="w-1/2" value="inbox">
          {t('inbox')}
        </TabsTrigger>
        <TabsTrigger className="w-1/2" value="sent">
          {t('sent')}
        </TabsTrigger>
      </TabsList>
      <TabsContent value="inbox">
        <ThreadList />
      </TabsContent>
      <TabsContent value="sent">
        <SentList />
      </TabsContent>
    </Tabs>
  )
}

const SelectMessageType = () => {
  const { t } = useTranslation('CoachMessageThread')
  const {
    setNewMessageType,
    setNewMessageMode,
    userIsCoach,
    coach_candidates,
    recipientCandidate,
    setRecipientCandidate,
  } = useMessaging()
  const cancelNewMessage = () => {
    setNewMessageType(null)
    setNewMessageMode(false)
  }

  const coachCandidatesById = useMemo(
    () =>
      Object.fromEntries(
        coach_candidates.map((candidate) => [String(candidate.id), candidate]),
      ),
    [coach_candidates],
  )

  return (
    <div className="flex flex-col gap-y-2 p-4">
      {userIsCoach && (
        <>
          <Select
            value={
              recipientCandidate ? String(recipientCandidate.id) : undefined
            }
            onValueChange={(value) =>
              setRecipientCandidate(coachCandidatesById[value] || null)
            }
          >
            <SelectTrigger>
              <SelectValue placeholder={'Select an Advantage User'} />
            </SelectTrigger>
            <SelectContent>
              {coach_candidates.map((candidate) => (
                <SelectItem
                  key={'coach_message' + candidate.id}
                  value={String(candidate.id)}
                >
                  {candidate.recipient_label ||
                    `${candidate.first_name} ${candidate.last_name}`}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button
            disabled={!recipientCandidate}
            onClick={() => setNewMessageType('coach')}
          >
            Message this Advantage User
          </Button>
        </>
      )}
      <Button variant="outline" onClick={cancelNewMessage}>
        {t('cancel')}
      </Button>
    </div>
  )
}

const MobileLayout = () => {
  const {
    newMessageMode,
    newMessageType,
    currentThreadId,
    viewingSentMessages,
  } = useMessaging()

  const currentMobileView = () => {
    if (newMessageMode) {
      if (newMessageType) {
        return (
          <div className="p-4">
            <NewMessage />
          </div>
        )
      } else {
        return <SelectMessageType />
      }
    } else if (viewingSentMessages) {
      return (
        <div className="p-4">
          <SentMessage />
        </div>
      )
    } else if (currentThreadId) {
      return (
        <div className="p-4">
          <MessageThread />
        </div>
      )
    } else {
      return <InboxSentTabs />
    }
  }
  return <div>{currentMobileView()}</div>
}

const DesktopLayout = ({ defaultLayout = [30, 70], layoutHeight }) => {
  const { newMessageType, newMessageMode, viewingSentMessages } = useMessaging()
  const NewMessageComp = newMessageType ? NewMessage : SelectMessageType
  const ViewMessageComp = viewingSentMessages ? SentMessage : MessageThread

  return (
    <ResizablePanelGroup direction="horizontal" className="h-full md:px-5">
      <ResizablePanel defaultSize={defaultLayout[0]} minSize={20}>
        <div className="pr-4 pt-4">
          <ScrollArea style={{ height: layoutHeight }}>
            <InboxSentTabs />
          </ScrollArea>
        </div>
      </ResizablePanel>
      <ResizableHandle withHandle className="bg-white" />
      <ResizablePanel
        defaultSize={defaultLayout[1]}
        minSize={30}
        style={{ height: layoutHeight, overflowY: 'auto' }}
        className="flex flex-col p-4"
      >
        {newMessageMode ? (
          <NewMessageComp />
        ) : (
          <ViewMessageComp layoutHeight={layoutHeight} />
        )}
      </ResizablePanel>
    </ResizablePanelGroup>
  )
}
